import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Search, Play, Square } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Colors, { withAlpha } from '../../constants/Colors';
import apiClient, { MockGeniusMilestone } from '../../services/api';

type Env = 'dev' | 'qa' | 'prod';
type GameType = 'football' | 'basketball';
type RunStatus = 'idle' | 'running' | 'done' | 'stopped';
type MilestoneState = 'pending' | 'active' | 'done' | 'error';

interface Milestone {
  label: string;
  state: MilestoneState;
  time?: string;
}

const MILESTONES_POLL_INTERVAL_MS = 4000;

const mapBackendStatus = (status: string): RunStatus => {
  const value = (status || '').toLowerCase();
  if (value.includes('error') || value.includes('fail') || value.includes('stop')) return 'stopped';
  if (value.includes('done') || value.includes('success') || value.includes('complete')) return 'done';
  return 'running';
};

const STATUS_STYLE: Record<RunStatus, { bg: string; fg: string; label: string }> = {
  idle: { bg: withAlpha(Colors.textLight, 0.14), fg: Colors.textLight, label: 'Idle' },
  running: { bg: 'rgba(255, 149, 0, 0.14)', fg: '#FF9500', label: 'Running' },
  done: { bg: withAlpha(Colors.success, 0.14), fg: Colors.success, label: 'Done' },
  stopped: { bg: withAlpha(Colors.error, 0.14), fg: Colors.error, label: 'Stopped' },
};

const MILESTONE_DOT_COLOR: Record<MilestoneState, string> = {
  pending: withAlpha(Colors.textLight, 0.35),
  active: '#FF9500',
  done: Colors.success,
  error: Colors.error,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '12px 16px',
};

export default function MockGeniusPanel() {
  const [env, setEnv] = useState<Env>('dev');
  const [gameType, setGameType] = useState<GameType>('football');
  const [gameName, setGameName] = useState('');
  const [gameId, setGameId] = useState('');
  const [gameIdFound, setGameIdFound] = useState<boolean | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const [inputFile, setInputFile] = useState('');
  const [homeAudioFile, setHomeAudioFile] = useState('');
  const [awayAudioFile, setAwayAudioFile] = useState('');
  const [runWindowSeconds, setRunWindowSeconds] = useState('900');
  const [audioMatchTipoff, setAudioMatchTipoff] = useState(false);
  const [audioMatchLeadSeconds, setAudioMatchLeadSeconds] = useState('1');

  const [dataFileOptions, setDataFileOptions] = useState<string[]>([]);
  const [audioFileOptions, setAudioFileOptions] = useState<string[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);
  const [filesError, setFilesError] = useState('');

  const [status, setStatus] = useState<RunStatus>('idle');
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [logs, setLogs] = useState('');
  const [starting, setStarting] = useState(false);
  const [startError, setStartError] = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setFilesLoading(true);
    setFilesError('');
    apiClient.mockGenius
      .getFiles()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setDataFileOptions(res.data.data_files || []);
          setAudioFileOptions(res.data.audio_files || []);
        } else {
          setFilesError(res.error || 'Failed to load files');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setFilesError(err instanceof Error ? err.message : 'Failed to load files');
      })
      .finally(() => {
        if (!cancelled) setFilesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const appendLog = (line: string) => {
    setLogs((prev) => `${prev}[${new Date().toLocaleTimeString()}] ${line}\n`);
  };

  const applyMilestonesResponse = (
    backendStatus: string,
    backendMilestones: MockGeniusMilestone[],
    backendLogs: string
  ) => {
    setMilestones(backendMilestones.map((m) => ({ label: m.label, state: m.state, time: m.time })));
    setLogs(backendLogs || '');
    const mapped = mapBackendStatus(backendStatus);
    setStatus(mapped);
    if (mapped !== 'running' && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const handleLookup = async () => {
    const trimmedName = gameName.trim();
    if (!trimmedName) return;
    setLookingUp(true);
    setGameId('');
    setGameIdFound(null);
    try {
      const res = await apiClient.mockGenius.lookupGame(trimmedName);
      if (res.success && res.data) {
        if (res.data.found && res.data.game_id) {
          setGameId(res.data.game_id);
          setGameIdFound(true);
        } else {
          setGameId('Not found');
          setGameIdFound(false);
        }
      } else {
        setGameId(`Lookup failed: ${res.error || 'unknown error'}`);
        setGameIdFound(false);
      }
    } catch (err) {
      setGameId(`Lookup failed: ${err instanceof Error ? err.message : 'unknown error'}`);
      setGameIdFound(false);
    } finally {
      setLookingUp(false);
    }
  };

  const pollMilestonesOnce = async () => {
    try {
      const res = await apiClient.mockGenius.getMilestones();
      if (res.success && res.data) {
        applyMilestonesResponse(res.data.status, res.data.milestones, res.data.logs);
      } else {
        appendLog(`Failed to fetch milestones: ${res.error || 'unknown error'}`);
      }
    } catch (err) {
      appendLog(`Failed to fetch milestones: ${err instanceof Error ? err.message : 'unknown error'}`);
    }
  };

  const getMissingFields = (): string[] => {
    const missing: string[] = [];
    if (!env) missing.push('Environment');
    if (!gameType) missing.push('Game Type');
    if (!gameName.trim()) missing.push('Game Name');
    if (!gameId.trim() || gameIdFound !== true) missing.push('Game ID (look up a valid game name first)');
    if (!inputFile) missing.push('Game Data File');
    if (!homeAudioFile && !awayAudioFile) missing.push('At least one Audio File (Home or Away)');
    const parsedWindow = Number(runWindowSeconds);
    if (!runWindowSeconds.trim() || !Number.isFinite(parsedWindow) || parsedWindow <= 0) {
      missing.push('Tip-off Run Time Window');
    }
    return missing;
  };

  const handleStart = async () => {
    const missing = getMissingFields();
    if (missing.length > 0) {
      setStartError(`Cannot start - missing required fields: ${missing.join(', ')}`);
      return;
    }
    setStartError('');

    if (pollRef.current) clearInterval(pollRef.current);

    setStarting(true);
    try {
      const res = await apiClient.mockGenius.startGame({
        env,
        game_type: gameType,
        game_name: gameName.trim(),
        game_id: gameId.trim(),
        input_file: inputFile,
        home_audio_file: homeAudioFile,
        away_audio_file: awayAudioFile,
        run_window_seconds: Number(runWindowSeconds),
        ...(audioMatchTipoff
          ? {
              audio_match_tipoff: true,
              audio_match_lead_seconds: Number(audioMatchLeadSeconds),
            }
          : {}),
      });
      if (!res.success) {
        setStartError(res.error || 'Failed to start game');
        return;
      }
    } catch (err) {
      setStartError(err instanceof Error ? err.message : 'Failed to start game');
      return;
    } finally {
      setStarting(false);
    }

    setStatus('running');
    setLogs('');
    setMilestones([]);
    appendLog(`Started game via /api/start (env=${env}, game_type=${gameType}, game_id=${gameId.trim()})`);
    appendLog(
      `Watching live orchestration status from /api/milestones (shared across all runs, not scoped to "${gameName.trim()}")`
    );

    pollMilestonesOnce();
    pollRef.current = setInterval(pollMilestonesOnce, MILESTONES_POLL_INTERVAL_MS);
  };

  const handleStop = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
    setStatus('stopped');
    setMilestones((prev) => prev.map((m) => (m.state === 'active' ? { ...m, state: 'error' } : m)));
    appendLog('Stopped watching orchestration status');
  };

  const isRunning = status === 'running';
  const missingFields = getMissingFields();
  const isFormValid = missingFields.length === 0;

  return (
    <div className="section">
      <h2 className="section-title">
        <Sparkles size={24} />
        Mock Genius
      </h2>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 340px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Card title="Milestones">
            <span
              style={{
                display: 'inline-block',
                padding: '3px 10px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 600,
                marginBottom: '16px',
                background: STATUS_STYLE[status].bg,
                color: STATUS_STYLE[status].fg,
              }}
            >
              {STATUS_STYLE[status].label}
            </span>

            {milestones.length === 0 ? (
              <p style={{ fontSize: '13px', color: Colors.textLight }}>Start a game to see milestones</p>
            ) : (
              <div>
                {milestones.map((m, i) => {
                  const isLast = i === milestones.length - 1;
                  return (
                    <div key={m.label} style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '10px', flexShrink: 0 }}>
                        <div
                          style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            background: MILESTONE_DOT_COLOR[m.state],
                            flexShrink: 0,
                            marginTop: '3px',
                          }}
                          className={m.state === 'active' ? 'mock-genius-dot active' : 'mock-genius-dot'}
                        />
                        {!isLast && (
                          <div style={{ width: '2px', flexGrow: 1, minHeight: '20px', background: withAlpha(Colors.textLight, 0.2) }} />
                        )}
                      </div>
                      <div style={{ paddingBottom: isLast ? 0 : '14px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: Colors.text }}>{m.label}</div>
                        {m.time && (
                          <div style={{ fontSize: '11px', color: Colors.textLight, fontFamily: "'SF Mono', monospace" }}>{m.time}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card title="Logs">
            <div
              style={{
                maxHeight: '260px',
                overflowY: 'auto',
                fontFamily: "'SF Mono', monospace",
                fontSize: '11.5px',
                background: '#F7F9FF',
                padding: '10px',
                borderRadius: '8px',
                border: `1px solid ${withAlpha(Colors.border, 0.16)}`,
                whiteSpace: 'pre-wrap',
                color: Colors.text,
                minHeight: '60px',
              }}
            >
              {logs || <span style={{ color: Colors.textLight }}>No logs yet</span>}
            </div>
          </Card>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <Card title="Mock Game Orchestration" description="Generate mock game data and drive a game through orchestration end to end">
            <div className="form-group" style={rowStyle}>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <label className="form-label">Environment</label>
                <select className="form-input" value={env} onChange={(e) => setEnv(e.target.value as Env)} disabled={isRunning}>
                  <option value="dev">Dev</option>
                  <option value="qa">QA</option>
                  <option value="prod">Prod</option>
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <label className="form-label">Game Type</label>
                <select className="form-input" value={gameType} onChange={(e) => setGameType(e.target.value as GameType)} disabled={isRunning}>
                  <option value="football">Football</option>
                  <option value="basketball">Basketball</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Game Name</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  className="form-input"
                  value={gameName}
                  onChange={(e) => setGameName(e.target.value)}
                  placeholder="e.g. blue-devils-vs-legacy-fc-20260801"
                  disabled={isRunning}
                  style={{ flex: 1 }}
                />
                <Button variant="success" onClick={handleLookup} loading={lookingUp} disabled={!gameName.trim() || isRunning}>
                  <Search size={16} />
                  Lookup
                </Button>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Game ID (from homestream-games table)</label>
              <input
                className="form-input"
                readOnly
                value={gameId}
                placeholder="Enter game name and click Lookup"
                style={{
                  background: gameIdFound === true ? withAlpha(Colors.success, 0.1) : gameIdFound === false ? withAlpha(Colors.error, 0.1) : '#F0F0F0',
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Game Data File (CSV/NDJSON)</label>
              <select
                className="form-input"
                value={inputFile}
                onChange={(e) => setInputFile(e.target.value)}
                disabled={isRunning || filesLoading}
              >
                <option value="">{filesLoading ? '-- loading --' : '-- select --'}</option>
                {dataFileOptions.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              {filesError && (
                <p style={{ fontSize: '12px', color: Colors.error, marginTop: '4px' }}>{filesError}</p>
              )}
            </div>

            <div className="form-group" style={rowStyle}>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <label className="form-label">Home Audio File (MP3)</label>
                <select
                  className="form-input"
                  value={homeAudioFile}
                  onChange={(e) => setHomeAudioFile(e.target.value)}
                  disabled={isRunning || filesLoading}
                >
                  <option value="">{filesLoading ? '-- loading --' : '-- none (skip radio) --'}</option>
                  {audioFileOptions.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1, minWidth: '160px' }}>
                <label className="form-label">Away Audio File (MP3)</label>
                <select
                  className="form-input"
                  value={awayAudioFile}
                  onChange={(e) => setAwayAudioFile(e.target.value)}
                  disabled={isRunning || filesLoading}
                >
                  <option value="">{filesLoading ? '-- loading --' : '-- none (skip radio) --'}</option>
                  {audioFileOptions.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Tip-off Run Time Window (seconds)</label>
              <input
                className="form-input"
                type="number"
                value={runWindowSeconds}
                onChange={(e) => setRunWindowSeconds(e.target.value)}
                disabled={isRunning}
              />
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: isRunning ? 'default' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={audioMatchTipoff}
                  onChange={(e) => setAudioMatchTipoff(e.target.checked)}
                  disabled={isRunning}
                />
                Match GENIUS tip-off to audio (one-off test)
              </label>
              <p style={{ fontSize: '12px', color: Colors.textLight, margin: '6px 0 0' }}>
                When on, GENIUS tip-off is set from the audio-detected tip-off row instead of the game clock. Use for pre-cropped audio where you play the file and the ECS task launch time varies.
              </p>
            </div>

            {audioMatchTipoff && (
              <div className="form-group">
                <label className="form-label">GENIUS lead before audio tip-off (seconds)</label>
                <input
                  className="form-input"
                  type="number"
                  value={audioMatchLeadSeconds}
                  onChange={(e) => setAudioMatchLeadSeconds(e.target.value)}
                  disabled={isRunning}
                  min={0}
                />
                <p style={{ fontSize: '12px', color: Colors.textLight, margin: '6px 0 0' }}>
                  GENIUS fires this many seconds before the audio tip-off.
                </p>
              </div>
            )}

            {!isRunning && missingFields.length > 0 && (
              <p style={{ fontSize: '12px', color: Colors.textLight, marginTop: '8px', marginBottom: 0 }}>
                Required before starting: {missingFields.join(', ')}
              </p>
            )}
            {startError && (
              <p style={{ fontSize: '12px', color: Colors.error, marginTop: '8px', marginBottom: 0 }}>{startError}</p>
            )}

            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <Button
                onClick={handleStart}
                disabled={!isFormValid || isRunning}
                loading={starting}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                <Play size={16} />
                Start Game
              </Button>
              {isRunning && (
                <Button variant="danger" onClick={handleStop} style={{ flex: 1 }}>
                  <Square size={16} />
                  Stop Execution
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
